import { useNavigate } from 'react-router-dom'
import { convertToShorthand } from '../../helpers/convert_date'
import { EmptyState, Text } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import { ArrowsClockwise } from '@phosphor-icons/react'
import RowMenu from '../row_menu'
import useStore from '../../hooks/use_store'
import { statusColors } from '../../constants/status_colour_map'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useTableFilters from '../../hooks/use_table_filters'
import { useState } from 'react'
import EditModal from './edit_modal'
import DeleteConfirmationModal from './delete_confirmation_modal'
import SchemaMappingModal from './schema_mapping_modal'
import { CycleStatus } from '../../constants/cycle_statuses'
import { useConfirm } from '../../contexts/confirm_dialog'

const cycleDisplayName = (cycle) => cycle?.name || cycle?.description || 'Untitled cycle'

const CyclesTable = ({ queryKey, airlines = null }) => {
  const [updatingCycleID, setUpdatingCycleID] = useState(null)
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const filteringOptions = airlines
    ? [
        {
          label: 'Airline',
          key: 'airline_id',
          type: 'checkboxes',
          options: airlines.map((a) => ({ label: a.name, key: a.id })),
        },
      ]
    : null

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions,
    enableSearch: airlines !== null,
  })
  const { data: cycles, isLoading, error, refetch } = useQuery([queryKey, queryString])
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [showSchemaMappingModalWithData, setShowSchemaMappingModalWithData] = useState(false)
  const [cycle, setCycle] = useState(null)
  const navigate = useNavigate()
  const { deleteModel, updateModel } = useStore()

  const deleteCycle = (cycleToDelete) => {
    deleteModel.mutate({
      endpoint: 'cycles',
      id: cycleToDelete.id,
      deleteNoun: 'cycle',
      deleteName: cycleToDelete.name,
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, queryString])
      },
    })
  }

  const deleteCycleConfirmed = (cycleID, airlineGroupCode = null) => {
    deleteModel.mutate({
      endpoint: 'cycles',
      id: cycleID,
      params: airlineGroupCode ? `?airline_group_code=${airlineGroupCode}` : '',
      shouldDelete: true, // Skip confirmation prompt
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, queryString])
        setShowDeleteConfirmationModal(false)
      },
      onError: (error) => {
        setShowDeleteConfirmationModal(true)
      },
    })
  }

  const openCycle = (data) => {
    setUpdatingCycleID(data.id)
    updateModel.mutate(
      {
        endpoint: 'cycles',
        id: `${data.id}/open${data.status === CycleStatus.Closed ? '?reopen' : ''}`,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([queryKey, queryString])
        },
        onSettled: () => setUpdatingCycleID(null),
        onError: (error) => {
          if (error.response?.data?.data !== null) {
            const mappingData = error.response?.data?.data ?? []
            if (Object.keys(mappingData).includes('fieldMappings')) {
              setCycle(data)
              setShowSchemaMappingModalWithData(mappingData)
            }
          } else {
            console.error('error opening cycle', error)
          }
        },
      }
    )
  }

  const requestOpenCycle = async (data) => {
    const airlineCycles = await queryClient.fetchQuery(['cycles', `?airlineID=${data.airline_id}`])
    const openCycles = (airlineCycles ?? []).filter((item) => item.id !== data.id && item.status === CycleStatus.Open)
    if (openCycles.length > 0) {
      const isReopen = data.status === CycleStatus.Closed
      const accepted = await confirm({
        title: 'Are you sure?',
        message: (
          <>
            <Text as='span' variant='sm-medium' tone='primary'>
              {openCycles.map(cycleDisplayName).join(', ')}
            </Text>
            {` ${openCycles.length === 1 ? 'is' : 'are'} already open for this airline.`}
          </>
        ),
        confirmText: isReopen ? 'Re-open cycle' : 'Open cycle',
      })
      if (!accepted) return
    }
    openCycle(data)
  }

  const columns = [
    { key: 'name', header: 'Name', render: (data) => data.name },
    { key: 'airline_id', header: 'Airline', render: (data) => data.airline_name },
    { key: 'cycle', header: 'Description', render: (data) => data.description },
    { key: 'start_date', header: 'Start', render: (data) => convertToShorthand(data.start_date) },
    { key: 'end_date', header: 'End', render: (data) => convertToShorthand(data.end_date) },
    {
      key: 'status_counts',
      header: 'Counts',
      render: ({ status_counts, id }) =>
        updatingCycleID === id ? (
          <div className='w-full'>
            <ArrowsClockwise size={20} className='animate-spin' />
          </div>
        ) : Object.values(status_counts ?? {}).reduce((sum, count) => sum + count, 0) ? (
          <div className='flex flex-row font-medium space-x-2'>
            <div className={`${statusColors.new} pr-2 border-r-2 border-gray-200`} title='New'>
              {status_counts.new ?? 0}
            </div>
            <div className={`${statusColors.holdover} pr-2 border-r-2 border-gray-200`} title='Holdover'>
              {status_counts.holdover ?? 0}
            </div>
            <div className={`${statusColors.expiring} pr-2 border-r-2 border-gray-200`} title='Expiring'>
              {status_counts.expiring ?? 0}
            </div>
            <div className={`${statusColors.recalled} pr-2`} title='Recalled'>
              {status_counts.recalled ?? 0}
            </div>
          </div>
        ) : (
          ''
        ),
    },
    { key: 'status', header: 'Status', render: (data) => data.status_label },
    { key: 'schema', header: 'Schema', render: (data) => data.schema.name },
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.name}`}
          items={[
            {
              id: 'edit',
              label: 'Edit',
              disabled: data.is_locked,
              onSelect: () => {
                setCycle(data)
                setShowEditModal(true)
              },
            },
            {
              id: 'open-cycle',
              label: data.status === CycleStatus.Closed ? 'Re-Open Cycle' : 'Open Cycle',
              disabled: data.status === CycleStatus.Open,
              onSelect: () => requestOpenCycle(data),
            },
            {
              id: 'close-cycle',
              label: 'Close Cycle',
              disabled: data.status !== CycleStatus.Open,
              onSelect: () => {
                setUpdatingCycleID(data.id)
                updateModel.mutate(
                  { endpoint: 'cycles', id: `${data.id}/close` },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries([queryKey, queryString])
                    },
                    onSettled: () => setUpdatingCycleID(null),
                    onError: (error) => {
                      console.error('error closing cycle', error)
                    },
                  }
                )
              },
            },
            {
              id: 'delete',
              label: 'Delete',
              tone: 'danger',
              onSelect: () => {
                setCycle(data)
                if (data.status === CycleStatus.New) {
                  deleteCycle(data)
                } else {
                  setShowDeleteConfirmationModal(true)
                }
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <DataTable
        caption='Cycles'
        columns={columns}
        rows={cycles ?? []}
        rowKey={(data) => String(data.id)}
        onRowClick={(data) => data.id !== updatingCycleID && navigate(`/airlines/${data.airline_id}/cycles/${data.id}`)}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No cycles found!' detail='Create a new cycle to begin.' />}
        searching={searching}
        sorting={sorting}
        filtering={filtering}
      />
      {showEditModal && (
        <EditModal
          cycle={cycle}
          invalidateKey={queryKey}
          onClose={() => setShowEditModal(false)}
          disabled={cycle.status === CycleStatus.Open}
        />
      )}
      {showSchemaMappingModalWithData !== false && (
        <SchemaMappingModal
          cycle={cycle}
          mappingData={showSchemaMappingModalWithData}
          invalidateKeys={[queryKey, queryString]}
          onClose={() => setShowSchemaMappingModalWithData(false)}
        />
      )}
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          cycle={cycle}
          errors={
            deleteModel?.error?.response?.data?.errors ? Object.values(deleteModel?.error?.response?.data.errors) : null
          }
          onClose={() => setShowDeleteConfirmationModal(false)}
          onConfirm={deleteCycleConfirmed}
        />
      )}
    </>
  )
}

export default CyclesTable
